import { useEffect, useCallback, useRef } from 'react';

const DRAFT_PREFIX = 'quentari_draft_';
const DRAFT_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

interface DraftData {
  text: string;
  savedAt: number;
  questionId?: string;
}

function getDraftKey(sessionId: string, stepContext: string): string {
  return `${DRAFT_PREFIX}${sessionId}_${stepContext}`;
}

/**
 * Autosaves user input to localStorage, keyed by session + step context.
 * Provides restore/clear/copy helpers for conflict recovery.
 */
export function useStepDraft(sessionId: string | undefined, stepContext: string) {
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const key = sessionId ? getDraftKey(sessionId, stepContext) : '';

  // Save draft with debounce
  const saveDraft = useCallback(
    (text: string, questionId?: string) => {
      if (!key || !text.trim()) return;

      if (debounceRef.current) clearTimeout(debounceRef.current);

      debounceRef.current = setTimeout(() => {
        try {
          const data: DraftData = { text, savedAt: Date.now(), questionId };
          localStorage.setItem(key, JSON.stringify(data));
        } catch {
          // localStorage full or unavailable – silently ignore
        }
      }, 400); // 400ms debounce
    },
    [key],
  );

  // Restore draft (returns null if expired or missing)
  const restoreDraft = useCallback((): DraftData | null => {
    if (!key) return null;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const data: DraftData = JSON.parse(raw);
      if (Date.now() - data.savedAt > DRAFT_EXPIRY_MS) {
        localStorage.removeItem(key);
        return null;
      }
      return data;
    } catch {
      return null;
    }
  }, [key]);

  // Clear draft after successful submit
  const clearDraft = useCallback(() => {
    if (!key) return;
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }, [key]);

  // Copy draft text to clipboard (for conflict recovery)
  const copyDraftToClipboard = useCallback(async (): Promise<boolean> => {
    const draft = restoreDraft();
    if (!draft?.text) return false;
    try {
      await navigator.clipboard.writeText(draft.text);
      return true;
    } catch {
      // Fallback for older browsers
      try {
        const textarea = document.createElement('textarea');
        textarea.value = draft.text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        return true;
      } catch {
        return false;
      }
    }
  }, [restoreDraft]);

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return { saveDraft, restoreDraft, clearDraft, copyDraftToClipboard };
}
